import { Localized } from "@robe/core";
import { Box, Typography } from "@xcorejs/ui";
import { useEffect, useMemo, useState } from "react";
import { useLayout } from "xcore";
import { EventValues, HappyHoursValues } from "xcore/types";

interface HappyHoursCountdownProps {
  values: HappyHoursValues;
  ongoingEventValues: EventValues;
}

export const HappyHoursCountdown = ({ values, ongoingEventValues }: HappyHoursCountdownProps) => {
  const { stringify, localize } = useLayout();

  const {
    targetDate: targetDateMemoized,
    timeDifference: initialTimeDifference,
    ...initialTimeLeft
  } = useMemo(() => {
    const targetDate = new Date();
    const { minutes, hours, day, month, year } = ongoingEventValues;

    if (minutes) {
      targetDate.setMinutes(+minutes);
      if (hours) {
        targetDate.setHours(minutes === "00" ? +hours - 1 : +hours);
      }
    }

    day && targetDate.setDate(+day);
    month && targetDate.setMonth(+month - 1);
    year && targetDate.setFullYear(+year);

    return {
      targetDate,
      ...calcTimeLeft(targetDate),
    };
  }, [ongoingEventValues]);

  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
  const [timeDifference, setTimeDifference] = useState(initialTimeDifference);

  const eventHasPassed = timeDifference < 0;

  useEffect(() => {
    if (eventHasPassed) {
      return;
    }

    const interval = setInterval(() => {
      const { timeDifference: calculatedTimeDifference, ...calculatedTimeLeft } = calcTimeLeft(targetDateMemoized);

      setTimeDifference(calculatedTimeDifference);
      setTimeLeft(calculatedTimeLeft);
    }, 1000);

    return () => clearInterval(interval);
  }, [eventHasPassed, targetDateMemoized]);

  if (eventHasPassed) {
    return null;
  }

  const resolveDateTimeValueLabel = (
    value: number,
    singular: Localized | undefined,
    plural234: Localized | undefined,
    plural: Localized | undefined
  ) => {
    if (value === 1) {
      return `${value} ${stringify(singular)}`;
    }

    const plural234Resolved = localize(plural234);

    if (plural234Resolved && value > 1 && value < 5) {
      return `${value} ${plural234Resolved}`;
    }

    return `${value} ${stringify(plural)}`;
  };

  const daysLabel = resolveDateTimeValueLabel(timeLeft.days, values.countdownDay, values.countdownDays234, values.countdownDaysPlural);
  const hoursLabel = resolveDateTimeValueLabel(timeLeft.hours, values.countdownHour, values.countdownHours234, values.countdownHoursPlural);
  const minutesLabel = resolveDateTimeValueLabel(
    timeLeft.minutes,
    values.countdownMinute,
    values.countdownMinutes234,
    values.countdownMinutesPlural
  );
  const secondsLabel = resolveDateTimeValueLabel(
    timeLeft.seconds,
    values.countdownSecond,
    values.countdownSeconds234,
    values.countdownSecondsPlural
  );

  return (
    <Box display="flex" flexDirection="row" style={{ gap: "30px" }} pt="5rem" pb="1.5rem">
      <Typography v="h2" textAlign="center">
        {daysLabel}
      </Typography>
      <Typography v="h2" textAlign="center">
        {hoursLabel}
      </Typography>
      <Typography v="h2" textAlign="center">
        {minutesLabel}
      </Typography>
      <Typography v="h2" textAlign="center">
        {secondsLabel}
      </Typography>
    </Box>
  );
};

const MINUTE_MILLISECONDS = 1000 * 60;
const HOUR_MILLISECONDS = MINUTE_MILLISECONDS * 60;
const DAY_MILLISECONDS = HOUR_MILLISECONDS * 24;

const calcTimeLeft = (targetDate: Date) => {
  const now = new Date();
  const timeDifference = targetDate.getTime() - now.getTime();

  return {
    days: Math.floor(timeDifference / DAY_MILLISECONDS),
    // eslint-disable-next-line @typescript-eslint/no-extra-parens
    hours: Math.floor((timeDifference % DAY_MILLISECONDS) / HOUR_MILLISECONDS),
    // eslint-disable-next-line @typescript-eslint/no-extra-parens
    minutes: Math.floor((timeDifference % HOUR_MILLISECONDS) / MINUTE_MILLISECONDS),
    // eslint-disable-next-line @typescript-eslint/no-extra-parens
    seconds: Math.floor((timeDifference % MINUTE_MILLISECONDS) / 1000),
    timeDifference,
  };
};
